import React from 'react';
import { motion } from 'framer-motion';
import { Music2, ArrowLeft, PlusCircle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

interface NotFoundProps {
  isLightMode?: boolean;
  message?: string;
  submessage?: string;
}

export function NotFound({ 
  isLightMode = false,
  message = "Challenge Not Found",
  submessage = "This custom challenge link is invalid, corrupted, or has expired."
}: NotFoundProps) {
  const navigate = useNavigate();

  return (
    <div className={cn(
      "min-h-screen flex flex-col items-center justify-center p-6 text-center transition-colors selection:bg-[#ff0080]/30",
      isLightMode ? "bg-[#f5f2ed] text-[#1a1a1a]" : "bg-[#181818] text-white"
    )}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="max-w-md w-full flex flex-col items-center"
      >
        {/* Vinyl / Broken Connection Graphic */}
        <div className="relative mb-6">
          <div className="w-24 h-24 rounded-full bg-[#ff0080]/10 border-2 border-[#ff0080]/30 flex items-center justify-center text-[#ff0080]">
            <Music2 className="w-12 h-12 stroke-[1.5]" />
          </div>
          <span className="absolute -top-1 -right-1 px-2.5 py-0.5 rounded-full text-[11px] font-black bg-[#ff0080] text-white shadow-lg">
            404
          </span>
        </div>

        <h1 className={cn("text-3xl font-black mb-3 tracking-tight", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
          {message}
        </h1>

        <p className={cn("text-sm mb-8 leading-relaxed", isLightMode ? "text-gray-600" : "text-gray-400")}>
          {submessage}
        </p>

        <div className="w-full space-y-3">
          <button
            onClick={() => navigate('/')}
            className="w-full py-4 rounded-full font-bold text-sm bg-[#ff0080] text-white hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-[#ff0080]/25 flex items-center justify-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            <span>Play Today's Daily Challenge</span>
          </button>

          <button
            onClick={() => navigate('/?create=1')}
            className={cn(
              "w-full py-3.5 rounded-full font-bold text-sm border transition-all flex items-center justify-center gap-2",
              isLightMode 
                ? "bg-white border-black/10 text-[#1a1a1a] hover:bg-gray-50" 
                : "bg-white/5 border-white/10 text-white hover:bg-white/10"
            )}
          >
            <PlusCircle className="w-4 h-4" />
            <span>Create Your Own Challenge</span>
          </button>

          <button
            onClick={() => navigate(-1)}
            className={cn(
              "w-full py-2.5 text-xs font-semibold flex items-center justify-center gap-1 transition-colors",
              isLightMode ? "text-gray-500 hover:text-gray-800" : "text-gray-400 hover:text-white"
            )}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Go Back</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
}
